import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import EmptyState from '../../components/common/EmptyState';
import { RestUser } from '../../../services/auth/types/user';
import { UserProfile } from '../../../types/types';
import {
  PublicProfile,
  getFollowersWithProfiles,
  getFollowingWithProfiles,
  getFollowRequests,
  acceptFollowRequest,
  declineFollowRequest,
  removeFollower,
  unfollowUser,
} from '../../../services/social/api';
import './ConnectionsPage.css';

interface ConnectionsPageProps {
  user: UserProfile;
  restUser: RestUser | null;
}

type Tab = 'followers' | 'following';

const isTab = (value: string | null): value is Tab =>
  value === 'followers' || value === 'following';

const fallbackAvatar = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
  <defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
    <stop stop-color="#0d4f3c"/><stop offset="1" stop-color="#d9ff31"/>
  </linearGradient></defs>
  <rect width="128" height="128" rx="64" fill="url(#g)"/>
  <circle cx="64" cy="48" r="23" fill="#f7efe6"/>
  <path d="M24 116c7-25 22-38 40-38s33 13 40 38" fill="#f7efe6"/>
</svg>`)}`;

const displayName = (profile: PublicProfile) =>
  [profile.firstName, profile.lastName].filter(Boolean).join(' ')
  || profile.nickname
  || profile.id;

/**
 * Connections management (T2.2c + D-35).
 *
 * Two tabs, each with its pending requests pinned on top: Followers (accept /
 * decline a follow request, remove a follower) and Following (unfollow).
 *
 * The Friends tab went with the friend graph on 2026-08-16 — friends and
 * followers described the same relationship, so it was a third tab restating
 * the first two. Reached from the profile-stats counters.
 */
const ConnectionsPage: React.FC<ConnectionsPageProps> = ({ restUser }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : 'followers';
  const myId = restUser?.id || '';

  const [requests, setRequests] = React.useState<PublicProfile[]>([]);
  const [followers, setFollowers] = React.useState<PublicProfile[]>([]);
  const [following, setFollowing] = React.useState<PublicProfile[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [busyId, setBusyId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!myId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getFollowRequests(myId).catch(() => ({ profiles: [] })),
      getFollowersWithProfiles(myId).catch(() => ({ profiles: [] })),
      getFollowingWithProfiles(myId).catch(() => ({ profiles: [] })),
    ])
      .then(([requestsPage, followersPage, followingPage]) => {
        if (cancelled) return;
        setRequests(requestsPage.profiles);
        setFollowers(followersPage.profiles);
        setFollowing(followingPage.profiles);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || 'Failed to load connections.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [myId]);

  const selectTab = (next: Tab) => setSearchParams({ tab: next }, { replace: true });

  const withBusy = async (id: string, action: () => Promise<void>) => {
    if (busyId) return;
    setBusyId(id);
    try {
      await action();
    } catch (err: any) {
      setError(err?.message || 'Action failed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleAccept = (profile: PublicProfile) => withBusy(profile.id, async () => {
    await acceptFollowRequest(profile.id);
    setRequests((current) => current.filter((p) => p.id !== profile.id));
    setFollowers((current) => [profile, ...current]);
  });

  const handleDecline = (profile: PublicProfile) => withBusy(profile.id, async () => {
    await declineFollowRequest(profile.id);
    setRequests((current) => current.filter((p) => p.id !== profile.id));
  });

  const handleRemoveFollower = (profile: PublicProfile) => withBusy(profile.id, async () => {
    await removeFollower(profile.id);
    setFollowers((current) => current.filter((p) => p.id !== profile.id));
  });

  const handleUnfollow = (profile: PublicProfile) => withBusy(profile.id, async () => {
    await unfollowUser(profile.id);
    setFollowing((current) => current.filter((p) => p.id !== profile.id));
  });


  const renderRow = (
    profile: PublicProfile,
    actions: React.ReactNode,
    highlight = false,
  ) => (
    <li
      key={profile.id}
      className={`v2-connections__row ${highlight ? 'v2-connections__row--request' : ''}`}
    >
      <Link to={`/user/${encodeURIComponent(profile.id)}`} className="v2-connections__identity">
        <img
          src={profile.pictureUrl || fallbackAvatar}
          alt=""
          onError={(event) => { event.currentTarget.src = fallbackAvatar; }}
        />
        <span>
          <strong>{displayName(profile)}</strong>
          {profile.nickname && <small>@{profile.nickname}</small>}
        </span>
      </Link>
      <div className="v2-connections__actions">{actions}</div>
    </li>
  );

  return (
    <DashboardLayout activeSection="feed">
      <div className="v2-connections">
        <header className="v2-connections__header">
          <button type="button" className="v2-connections__back" onClick={() => navigate('/profile')}>
            ← Profile
          </button>
          <h1>Connections</h1>
        </header>

        <div className="v2-connections__tabs" role="tablist" aria-label="Connections">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'followers'}
            className={tab === 'followers' ? 'v2-connections__tab--active' : ''}
            onClick={() => selectTab('followers')}
          >
            Followers
            {requests.length > 0 && (
              <span className="v2-connections__tab-badge">{requests.length}</span>
            )}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'following'}
            className={tab === 'following' ? 'v2-connections__tab--active' : ''}
            onClick={() => selectTab('following')}
          >
            Following
          </button>
        </div>

        {loading && <p className="v2-connections__state">Loading…</p>}
        {!loading && error && <p className="v2-connections__state v2-connections__state--error">{error}</p>}

        {!loading && !error && tab === 'followers' && (
          <ul className="v2-connections__list">
            {requests.map((profile) => renderRow(
              profile,
              <>
                <button
                  type="button"
                  className="v2-connections__primary"
                  disabled={busyId === profile.id}
                  onClick={() => handleAccept(profile)}
                >
                  Accept request
                </button>
                <button
                  type="button"
                  className="v2-connections__secondary"
                  disabled={busyId === profile.id}
                  onClick={() => handleDecline(profile)}
                >
                  Decline
                </button>
              </>,
              true,
            ))}
            {followers.map((profile) => renderRow(
              profile,
              <button
                type="button"
                className="v2-connections__secondary"
                disabled={busyId === profile.id}
                onClick={() => handleRemoveFollower(profile)}
              >
                Remove
              </button>,
            ))}
            {requests.length === 0 && followers.length === 0 && (
              /* Wrapped in <li> because these live inside the <ul> above — a bare
                 <p> there was invalid markup. */
              <li>
                <EmptyState
                  compact
                  title="No followers yet"
                  body="People who follow you appear here. The fastest way to get found is to make a trip public — it shows up in Explore for everyone."
                  action={{ label: 'Browse Explore', to: '/explore' }}
                />
              </li>
            )}
          </ul>
        )}

        {!loading && !error && tab === 'following' && (
          <ul className="v2-connections__list">
            {following.map((profile) => renderRow(
              profile,
              <button
                type="button"
                className="v2-connections__secondary"
                disabled={busyId === profile.id}
                onClick={() => handleUnfollow(profile)}
              >
                Unfollow
              </button>,
            ))}
            {following.length === 0 && (
              <li>
                <EmptyState
                  compact
                  title="You aren’t following anyone yet"
                  body="Following someone puts their posts in your feed. Open a trip you like in Explore and follow whoever planned it."
                  action={{ label: 'Find travellers', to: '/explore' }}
                />
              </li>
            )}
          </ul>
        )}

      </div>
    </DashboardLayout>
  );
};

export default ConnectionsPage;
